import { ReactElement } from "react";
import { Box, SxProps, Theme, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useTranslation } from "react-i18next";
import TheGamePill from "./thegamepill";
import { marketSigns, TheGameMarket, toCouponSign } from "./thegamemanifest";
import { getMarketLabel, getSelectionLabel } from "./thegamemarkets";
import { CORRECT, CORRECT_SOFT, LINE, MarketResult, MarketState, marketCardSx, ON_CORRECT, WRONG, WRONG_SOFT } from "./thegamecommon";

const resultChipSx: Partial<Record<MarketResult, SxProps<Theme>>> = {
  won: { background: CORRECT, color: ON_CORRECT },
  hit: { background: CORRECT_SOFT, color: CORRECT },
  miss: { background: WRONG_SOFT, color: WRONG },
  lost: { background: WRONG_SOFT, color: WRONG },
};

const resultTextKey: Partial<Record<MarketResult, string>> = {
  won: "tg_won",
  hit: "correct_now",
  miss: "not_now",
  lost: "tg_lost",
};

interface TheGameLiveMarketProps {
  market: TheGameMarket;
  state: MarketState;
  showResult: boolean;
  // Row share per coupon sign (ONE/X/TWO) for the spectator view.
  shares?: Partial<Record<string, number>>;
}

export default function TheGameLiveMarket({ market, state, showResult, shares }: TheGameLiveMarketProps): ReactElement {
  const { t } = useTranslation();
  const signs = marketSigns(market);
  const chipKey = showResult ? resultTextKey[state.result] : undefined;

  return (
    <Box component="section" sx={marketCardSx}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px", minWidth: 0 }}>
          <Typography sx={{ fontFamily: "var(--font-body)", fontSize: 16, fontWeight: 600 }}>{getMarketLabel(market, t)}</Typography>
          {state.note && (
            <Box
              component="span"
              sx={{ fontSize: 13, fontWeight: 600, color: "var(--font-color-supportive)", border: `1px solid ${LINE}`, borderRadius: "12px", padding: "1px 8px", whiteSpace: "nowrap" }}
            >
              {state.note}
            </Box>
          )}
        </Box>
        {chipKey && (
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: 13,
              fontWeight: 600,
              borderRadius: "12px",
              padding: "2px 8px",
              whiteSpace: "nowrap",
              ...(resultChipSx[state.result] as object),
            } as SxProps<Theme>}
          >
            {state.result === "won" && <CheckRoundedIcon sx={{ fontSize: 14 }} />}
            {t(chipKey)}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${signs.length}, minmax(0, 1fr))`, gap: "8px" }}>
        {signs.map((sign) => {
          const couponSign = toCouponSign(sign);
          const isCurrent = state.current === couponSign;
          return (
            <TheGamePill
              key={sign}
              label={getSelectionLabel(market, sign, t)}
              mine={state.mine.includes(couponSign)}
              lead={!state.decided && isCurrent}
              locked={state.decided && isCurrent}
              out={state.decided && !isCurrent}
              share={shares?.[couponSign]}
            />
          );
        })}
      </Box>
    </Box>
  );
}
